/**
 * Cooperative activities protocol (social-systems): "Night Freight" — a crew buys a shipment at the
 * Gullet Basin docks, splits it physically across up to three vehicles and runs it to a buyer in
 * another district (or the organiser's lockup) while police attention rises and a rumour tempts rival
 * crews to intercept. See docs/ACTIVITIES.md.
 *
 * Clients send intent only; the server decides participants, roles, cargo, price and payouts.
 */
import { s } from '../schema';

const nonce = s.str({ min: 8, max: 64, pattern: /^[A-Za-z0-9_-]+$/ });

/** Tuning (shared so the client can show it; the server is authoritative). */
export const FREIGHT = {
  /** Where shipments are bought (POI). */
  pierPoi: 'docks-bonded-3',
  /** Crew members and vehicles must be this close to the pier to join a run. */
  pierRadius: 35,
  /** Buyer contacts (POIs) in other districts; one is picked per run. */
  contactPois: ['gasworks-yard', 'st-brannocs', 'scrapyard-gorringe'],
  /** Delivery reach around the destination (plus the POI's own radius). */
  destReach: 15,
  /** The shipment good: crates of stolen fur coats (`velvet`), Fine quality (so stock from street suppliers never counts). */
  def: 'velvet',
  quality: 1,
  /** Crates per vehicle (fewer if the boot is smaller). */
  cratesPerVehicle: 4,
  maxVehicles: 3,
  /** A run needs at least this many players (it is a crew activity). */
  minPlayers: 2,
  /** Price per crate at the pier and what the contact pays per delivered crate. */
  crateCost: 300,
  cratePrice: 650,
  /** Gang-funded runs: share of the profit that goes back to the treasury. */
  gangCut: 0.2,
  /** Police get wind of it this long after the start: district attention + a rumour to other players. */
  alertDelayMs: 45_000,
  alertPoints: 14,
  /** The rumour points vaguely at the pier: rounded / jittered, with this radius. */
  rumourRadius: 140,
  timeLimitMs: 8 * 60_000,
  /** A participant counts as "with the convoy" within this distance of a shipment vehicle. */
  convoyRadius: 150,
  /** The lookout gets intel while within this distance of the convoy, about things within intelRadius. */
  lookoutRange: 150,
  intelRadius: 220,
  /** Same organiser can start again after this. */
  cooldownMs: 10 * 60_000,
  maxActivePerShard: 2,
  /** Role weights for the 'role' split (multiplied by time spent with the convoy). */
  weights: { driver: 1.5, lookout: 1.2, escort: 1 },
  /**
   * Hourly cap (WP-N1, docs/ACTIVITIES.md "The hourly cap"): each player's cut is paid in full up to
   * `fullPayPerHour` pounds per rolling `capWindowMs`; whatever is over it is paid at `overCapRate`. Per
   * character, across every run, crew, organiser and shard (the earnings are kept in the database). A treasury
   * run's gang cut shrinks in the same proportion as its members' cuts. Stakes are refunds, never capped.
   * Sized so a pair alternating organisers flat out stays under the documented ceiling (~£8.3k/h a head,
   * `npx tsx server/economy/balance.ts` asserts it).
   */
  fullPayPerHour: 5_000,
  overCapRate: 0.15,
  capWindowMs: 60 * 60_000,
} as const;

/** Your full-pay allowance for Night Freight right now (the hourly cap). */
export interface FreightAllowance {
  /** Full pay per rolling hour. */
  cap: number;
  /** Full pay still left in the current hour. */
  left: number;
  /** Over the cap, a cut is paid at this share. */
  rate: number;
  /** Server time when some of the used allowance comes back (absent while nothing is used). */
  nextAt?: number;
}

/**
 * Pure: a cut of `base` pounds against `left` full-pay pounds — the part paid in full, the rest at the
 * over-cap rate (floored to whole pounds).
 */
export function freightCapSplit(base: number, left: number): { full: number; paid: number } {
  const full = Math.max(0, Math.min(base, Math.floor(left)));
  return { full, paid: full + Math.floor((base - full) * FREIGHT.overCapRate) };
}

export type FreightRole = 'driver' | 'lookout' | 'escort';
export type FreightSplit = 'equal' | 'role';

export const ActivityClientSchemas = {
  /** Organiser (at the pier, with their crew) buys the shipment and starts the run. */
  freightStart: s.obj({
    pay: s.lit('cash', 'treasury'),
    dest: s.lit('buyer', 'lockup'),
    /** Lockup property id (dest 'lockup'). */
    lockup: s.opt(s.int({ min: 1 })),
    split: s.lit('equal', 'role'),
    n: nonce,
  }),
  /** Driver of a shipment vehicle at the destination hands over its crates. */
  freightDeliver: s.empty(),
  freightState: s.empty(),
  /** Lookout warns the crew (a ping at the lookout's position). */
  freightWarn: s.empty(),
  /** Organiser calls it off: whatever was delivered is paid; crates still in vehicles stay there. */
  freightAbort: s.empty(),
};

export interface FreightVehicleView {
  eid: number;
  spec: string;
  crates: number;
  delivered: number;
  /** 'hijacked' = someone outside the crew is driving it. 'lost' = wrecked, gone, or emptied. */
  status: 'active' | 'delivered' | 'lost' | 'hijacked';
  x: number;
  z: number;
}

export interface FreightMemberView {
  cid: number;
  name: string;
  role: FreightRole;
  connected: boolean;
  /** Left the world (grace ran out / quit): share forfeit. */
  left: boolean;
}

export interface FreightPayout {
  cid: number | null;
  /** 'share' | 'stake' (funder reimbursed for delivered cargo) | 'gang' (treasury cut). */
  kind: 'share' | 'stake' | 'gang';
  amount: number;
  /** Set when the hourly cap cut it: what it would have been at full pay. */
  base?: number;
}

export interface FreightView {
  id: string;
  status: 'active' | 'completed';
  organiser: number;
  gang: number | null;
  pay: 'cash' | 'treasury';
  split: FreightSplit;
  dest: { kind: 'buyer' | 'lockup'; poi: string; name: string; x: number; z: number; district: string };
  startedAt: number;
  deadline: number;
  alerted: boolean;
  cost: number;
  cratePrice: number;
  crates: number;
  delivered: number;
  vehicles: FreightVehicleView[];
  members: FreightMemberView[];
  you: { role: FreightRole } | null;
  /** Set once completed. `held`: pounds the hourly cap kept back (gross − held = everything paid). */
  outcome?: { reason: string; gross: number; payouts: FreightPayout[]; held?: number };
}

export interface FreightIntel {
  at: number;
  police: { x: number; z: number }[];
  rivals: { x: number; z: number; wanted: boolean }[];
}

declare module '../protocol' {
  interface ClientMessageMap {
    freightStart: { pay: 'cash' | 'treasury'; dest: 'buyer' | 'lockup'; lockup?: number; split: FreightSplit; n: string };
    freightDeliver: Record<string, never>;
    freightState: Record<string, never>;
    freightWarn: Record<string, never>;
    freightAbort: Record<string, never>;
  }
  interface ServerMessageMap {
    /** Your run changed (null = you are in none). `allowance`: your hourly full-pay allowance (after a payout). */
    freight: { run: FreightView | null; reason?: string; allowance?: FreightAllowance };
    /** Lookout only: police and outsiders near the convoy. */
    freightIntel: FreightIntel;
    /** The lookout raised a warning. */
    freightPing: { from: string; x: number; z: number };
    /** To everyone else in the shard: something valuable is moving. Vague on purpose. */
    freightRumour: { text: string; x: number; z: number; radius: number };
  }
}
